import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Card } from "@/components/ui/card";
import { StatusBadge } from "@/components/StatusBadge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  CartesianGrid,
} from "recharts";
import { Shield, ShieldAlert, Activity, Layers, ShieldCheck, Laptop, BookOpen, ArrowRight, Lock, Fingerprint, Globe2, Clock } from "lucide-react";
import { motion, useMotionValue, animate } from "framer-motion";
import { useNavigate } from "react-router-dom";

const CAT_COLORS = ["#00E5FF", "#00FF88", "#FF9900", "#FF3B30", "#8B5CF6", "#0EA5E9"];

/* ═══════════════════════════════════════════
   ADMIN / AUDITEUR DASHBOARD (full SIEM)
   ═══════════════════════════════════════════ */
function AdminDashboard({ user, t }) {
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);

  const load = async () => {
    const [s, a, l] = await Promise.all([
      api.get("/siem/stats"),
      api.get("/siem/alerts", { params: { status: "open" } }),
      api.get("/siem/logs", { params: { limit: 30 } }),
    ]);
    setStats(s.data);
    setAlerts(a.data);
    setLogs(l.data);
  };

  useEffect(() => {
    load();
    const token = localStorage.getItem("dndi_token");
    if (!token) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    const wsUrl = backend.replace(/^http/, "ws") + `/api/siem/ws?token=${token}&mode=${user?.mode}`;
    const socket = new WebSocket(wsUrl);
    socket.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "log") {
          setLogs((prev) => [msg.log, ...prev].slice(0, 30));
        } else if (msg.type === "alert") {
          setAlerts((prev) => [msg.alert, ...prev]);
        }
      } catch {}
    };
    return () => socket.close();
  }, [user?.mode]);

  const catData = stats
    ? Object.entries(stats.by_category).map(([name, value]) => ({ name, value }))
    : [];
  const sevData = stats
    ? Object.entries(stats.by_severity).map(([name, value]) => ({ name, value }))
    : [];

  return (
    <div data-testid="dashboard-page" className="animate-fade-in-up cyber-grid-bg space-y-6 relative min-h-full pb-4">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40">
          {t("dashboard")}
        </div>
        <h1 className="text-3xl font-semibold tracking-tight mt-1 glitch-text">
          Bonjour {user?.name?.split(" ")[0]}
        </h1>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Événements SIEM" value={stats?.total_logs ?? 0} icon={Activity} color="text-[#00E5FF]" progressColor="bg-[#00E5FF]" testId="kpi-total-logs" delay={0.1} />
        <Kpi label="Accès autorisés" value={stats?.pdp_allow ?? 0} icon={Shield} color="text-[#00FF88]" progressColor="bg-[#00FF88]" testId="kpi-allow" delay={0.2} />
        <Kpi label="Accès refusés" value={stats?.pdp_deny ?? 0} icon={ShieldAlert} color="text-[#FF3B30]" progressColor="bg-[#FF3B30]" testId="kpi-deny" delay={0.3} />
        <Kpi label="Alertes ouvertes" value={stats?.alerts_open ?? 0} icon={Layers} color="text-[#FF9900]" progressColor="bg-[#FF9900]" testId="kpi-alerts" delay={0.4} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="col-span-1 lg:col-span-2">
          <Card className="cyber-card cyber-glass rounded-sm p-4 h-full">
            <div className="text-xs font-mono uppercase tracking-[0.2em] glitch-text text-white/60 mb-3">
              Événements par catégorie
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={catData}>
                  <CartesianGrid stroke="#ffffff10" vertical={false} />
                  <XAxis dataKey="name" stroke="#ffffff60" fontSize={10} />
                  <YAxis stroke="#ffffff60" fontSize={10} />
                  <Tooltip contentStyle={{ background: "#050a0e", border: "1px solid #00E5FF", borderRadius: 2, boxShadow: "0 0 10px #00E5FF40" }} />
                  <Bar dataKey="value" fill="#00E5FF" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          <Card className="cyber-card cyber-glass rounded-sm p-4 h-full">
            <div className="text-xs font-mono uppercase tracking-[0.2em] glitch-text text-white/60 mb-3">
              Sévérité
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={sevData} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} stroke="#050a0e">
                    {sevData.map((_, i) => (
                      <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#050a0e", border: "1px solid #00E5FF", borderRadius: 2, boxShadow: "0 0 10px #00E5FF40" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Feeds */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}>
          <Card className="cyber-card cyber-glass rounded-sm p-4 h-full" data-testid="feed-alerts">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="live-dot" />
                <div className="text-xs font-mono uppercase tracking-[0.2em] glitch-text text-white/60">
                  {t("open_alerts")}
                </div>
              </div>
              <StatusBadge status="critical">{alerts.length}</StatusBadge>
            </div>
            <div className="space-y-2 max-h-72 overflow-auto pr-1">
              {alerts.length === 0 && (
                <div className="text-xs text-white/40 py-8 text-center">Aucune alerte active.</div>
              )}
              {alerts.map((a) => (
                <motion.div 
                  key={a._id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="flash-new border border-white/10 rounded-sm p-2 hover:bg-white/[0.05] transition-colors cyber-glass"
                >
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-white/90 flex items-center gap-2">
                      {a.severity === "critical" && <span className="critical-dot" />}
                      {a.message}
                    </div>
                    <StatusBadge status={a.severity} />
                  </div>
                  <div className="mt-1 text-[10px] font-mono text-white/40">
                    {a.kind} · {new Date(a.timestamp).toLocaleString()}
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}>
          <Card className="cyber-card cyber-glass rounded-sm p-4 h-full" data-testid="feed-logs">
            <div className="flex items-center gap-2 mb-3">
              <div className="live-dot" />
              <div className="text-xs font-mono uppercase tracking-[0.2em] glitch-text text-white/60">
                LIVE FEED
              </div>
            </div>
            <div className="space-y-1 max-h-72 overflow-auto pr-1 font-mono text-[11px]">
              {logs.map((l) => (
                <motion.div 
                  key={l._id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flash-new flex items-start gap-2 py-1 border-b border-white/5"
                >
                  <span className="text-white/40 shrink-0">{new Date(l.timestamp).toLocaleTimeString()}</span>
                  <StatusBadge status={l.severity} className="shrink-0" />
                  <span className="text-[#00E5FF] shrink-0">{l.category}</span>
                  <span className="text-white/60">{l.action}</span>
                  <span className="text-white/40 truncate">{l.user_email || ""}</span>
                </motion.div>
              ))}
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════
   USER DASHBOARD (welcome + quick access)
   ═══════════════════════════════════════════ */
function UserDashboard({ user, t }) {
  const navigate = useNavigate();
  const [posture, setPosture] = useState(null);
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";

  useEffect(() => {
    const detectPosture = async () => {
      try {
        const { collectClientHints, requestGeolocation } = await import("@/lib/clientHints");
        const hints = await collectClientHints();
        const geo = await requestGeolocation();
        if (geo.permission) {
          hints.geo_lat = geo.lat;
          hints.geo_lon = geo.lon;
          hints.geo_permission = true;
        } else {
          hints.geo_permission = false;
        }
        const { data } = await api.post("/posture/detect", { client_hints: hints });
        setPosture(data);
      } catch {}
    };
    detectPosture();
  }, []);

  const quickLinks = [
    {
      icon: BookOpen,
      title: t("catalog"),
      desc: "Accédez aux ressources médicales sécurisées",
      path: "/app/catalog",
      color: "#00E5FF",
      delay: 0.2,
    },
    {
      icon: Laptop,
      title: t("devices"),
      desc: "Consultez la posture de sécurité de vos appareils",
      path: "/app/devices",
      color: "#00FF88",
      delay: 0.3,
    },
    {
      icon: Fingerprint,
      title: "Mon profil de sécurité",
      desc: "Authentification MFA active et vérifiée",
      path: null,
      color: "#8B5CF6",
      delay: 0.4,
    },
  ];

  return (
    <div data-testid="dashboard-page" className="animate-fade-in-up relative min-h-full pb-4">
      {/* Welcome Hero */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative overflow-hidden rounded-sm cyber-card p-8 mb-8"
      >
        {/* Background decorations */}
        <div className="absolute inset-0 cyber-grid-bg opacity-40 pointer-events-none" />
        <div className="absolute top-0 right-0 w-72 h-72 opacity-10 pointer-events-none"
          style={{ background: "radial-gradient(circle, #00E5FF 0%, transparent 70%)" }}
        />

        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-3 mb-2"
          >
            <div className="w-2 h-2 rounded-full bg-green-500 live-dot" />
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-green-400 font-bold">
              SESSION SÉCURISÉE
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl font-bold tracking-tight mb-2"
          >
            {greeting},{" "}
            <span className="cyber-glow-text">{user?.name?.split(" ")[0]}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-white/50 text-sm max-w-lg"
          >
            Bienvenue sur la console Zero Trust de DNDi. Votre accès est protégé par une authentification multi-facteurs et une vérification continue de la posture de votre appareil.
          </motion.p>

          {/* User info pills */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap gap-3 mt-6"
          >
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-cyan-500/20 bg-cyan-500/5">
              <Shield className="h-3.5 w-3.5 text-cyan-400" />
              <span className="text-[11px] font-mono text-cyan-300 uppercase tracking-wider">{user?.role}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-white/10 bg-white/[0.02]">
              <Globe2 className="h-3.5 w-3.5 text-white/50" />
              <span className="text-[11px] font-mono text-white/60 uppercase tracking-wider">{user?.site}</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-green-500/20 bg-green-500/5">
              <Lock className="h-3.5 w-3.5 text-green-400" />
              <span className="text-[11px] font-mono text-green-300 uppercase tracking-wider">MFA ACTIVE</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-white/10 bg-white/[0.02]">
              <Clock className="h-3.5 w-3.5 text-white/50" />
              <span className="text-[11px] font-mono text-white/60 uppercase tracking-wider">
                {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
              </span>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Quick Access Cards */}
      <div className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-4">
          Accès rapide
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {quickLinks.map((link, i) => {
            const Icon = link.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: link.delay }}
                whileHover={link.path ? { scale: 1.02, y: -2 } : {}}
                onClick={() => link.path && navigate(link.path)}
                className={`cyber-card p-5 group ${link.path ? "cursor-pointer" : ""}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-10 h-10 rounded-sm flex items-center justify-center"
                    style={{ background: `${link.color}10`, border: `1px solid ${link.color}30` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: link.color, filter: `drop-shadow(0 0 6px ${link.color})` }} />
                  </div>
                  {link.path && (
                    <ArrowRight className="h-4 w-4 text-white/20 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
                  )}
                </div>
                <h3 className="text-sm font-semibold text-white/90 mb-1">{link.title}</h3>
                <p className="text-xs text-white/40 leading-relaxed">{link.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Device Security Status — uses same fields as Catalog */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/40 mb-4">
          {t("device_posture")}
        </div>
        <Card className="cyber-card cyber-glass rounded-sm p-5">
          {!posture ? (
            <div className="text-xs text-white/40 font-mono py-4 text-center">Analyse du terminal en cours…</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <DashPostureItem
                label={t("os_updated")}
                ok={posture.os_updated}
                value={posture.os_updated ? "OUI" : "NON"}
                detail={posture.os ? `${posture.os} ${posture.os_version || ""}`.trim() : null}
                delay={0.7}
              />
              <DashPostureItem
                label={t("antivirus")}
                ok={posture.antivirus}
                value={posture.antivirus ? "ACTIF" : "NON DÉTECTÉ"}
                delay={0.8}
              />
              <DashPostureItem
                label={t("disk_encryption")}
                ok={posture.disk_encryption}
                value={posture.disk_encryption ? "ACTIVÉ" : "INCONNU"}
                delay={0.9}
              />
              <DashPostureItem
                label={t("trusted_network")}
                ok={posture.trusted_network}
                value={posture.trusted_network ? "OUI" : "NON"}
                detail={posture.location_country && posture.location_country !== "??" ? `Pays : ${posture.location_country}` : null}
                delay={1.0}
              />
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}

function DashPostureItem({ label, ok, value, detail, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      className={`flex items-center gap-3 p-3 rounded-sm border ${
        ok ? "border-emerald-500/20 bg-emerald-500/[0.03]" : "border-rose-500/25 bg-rose-500/[0.03]"
      }`}
    >
      <div className={ok ? "live-dot" : "critical-dot"} />
      <div>
        <div className="text-xs text-white/70">{label}</div>
        <div className={`text-[10px] font-mono uppercase tracking-wider ${ok ? "text-green-400" : "text-red-400"}`}>
          {value || (ok ? "OK" : "ALERTE")}
        </div>
        {detail && <div className="text-[9px] font-mono text-white/30 mt-0.5">{detail}</div>}
      </div>
    </motion.div>
  );
}


/* ═══════════════════════════════════════════
   MAIN EXPORT — routes to correct dashboard
   ═══════════════════════════════════════════ */
export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useLang();

  const isAdmin = user?.role === "admin" || user?.role === "auditeur";

  if (isAdmin) {
    return <AdminDashboard user={user} t={t} />;
  }
  return <UserDashboard user={user} t={t} />;
}


/* ═══════════════════════════════════════════
   KPI CARD (admin dashboard only)
   ═══════════════════════════════════════════ */
function Kpi({ label, value, icon: Icon, color, progressColor, testId, delay }) {
  const count = useMotionValue(0);
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const animation = animate(count, value, {
      duration: 1.5,
      onUpdate: (latest) => setDisplayValue(Math.floor(latest)),
    });
    return animation.stop;
  }, [value, count]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <Card className="cyber-card cyber-glass rounded-sm p-4 relative overflow-hidden" data-testid={testId}>
        <div className="flex items-center justify-between relative z-10">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">{label}</div>
          <Icon className={`h-4 w-4 ${color}`} style={{ filter: "drop-shadow(0 0 8px currentColor)" }} />
        </div>
        <div className="mt-2 text-3xl font-semibold tracking-tight relative z-10">{displayValue}</div>
        
        <div className="mt-3 w-full h-[2px] bg-white/10 rounded overflow-hidden">
          <motion.div 
            className={`h-full ${progressColor}`} 
            initial={{ width: 0 }} 
            animate={{ width: "100%" }} 
            transition={{ delay: delay + 0.5, duration: 1 }}
          />
        </div>
      </Card>
    </motion.div>
  );
}
